import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with plugins for enhanced functionality
// for handling dates and times in various ways
dayjs.extend(utc); //  to manipulate and format dates in Coordinated Universal Time (UTC)
dayjs.extend(timezone); // to work with different time zones, enabling the conversion of dates and times between time zones
dayjs.extend(relativeTime); // for formatting dates and times in a relative manner, such as "3 days ago" or "in 2 hours."
dayjs.extend(localizedFormat); //for the use of localized date and time formats based on user's locale

const DateFormats: { [key: string]: string } = {
  'DD MMM YYYY': 'DD MMM YYYY',
  'DD MMM': 'DD MMM',
  'DD MMMM YYYY': 'DD MMMM YYYY',
  'DD MMMM': 'DD MMMM',
  'MMM DD, YYYY': 'MMM DD, YYYY',
  'MMMM DD, YYYY': 'MMMM DD, YYYY',
  'DD-MM-YYYY': 'DD-MM-YYYY',
  'MM/DD/YYYY': 'MM/DD/YYYY',
};

/**
 * Formats a timestamp dynamically based on how long ago it was
 * @param date The date to format (string or Date object)
 * @param momentInstance Optional moment instance for testing
 * @param nowDate Optional current date for testing
 * @returns Formatted string representing the relative time
 */
export function formatDynamicTimestamp(date: string | Date, dateFormat?: string): string {
  // Use the provided moment instance (for testing) or the imported one
  const dayjsObj = dayjs;

  // Parse the input date
  let inputDate: dayjs.Dayjs;

  // If the date is a string and has timezone information or ends with Z, parse it as UTC and convert to local
  if (typeof date === 'string' && (date.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(date))) {
    inputDate = dayjsObj.utc(date).local();
  } else {
    // Otherwise, parse as local time
    inputDate = dayjsObj(date);
  }

  // Use provided now date or current time
  const now = dayjsObj();

  if (!inputDate.isValid()) {
    return 'invalid-date';
  }

  const diffInMinutes = now.diff(inputDate, 'minutes');
  const diffInHours = now.diff(inputDate, 'hours');
  const diffInDays = now.diff(inputDate, 'days');

  // Less than 1 minute ago
  if (diffInMinutes < 1) {
    return 'Just now';
  }

  // 1 minute ago
  if (diffInMinutes === 1) {
    return '1 min ago';
  }

  // Less than 1 hour ago
  if (diffInMinutes < 60) {
    return `${diffInMinutes} mins ago`;
  }

  // 1 hour ago
  if (diffInHours === 1) {
    return '1 hour ago';
  }

  // Less than 24 hours ago
  if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  }

  // 1 day ago or yesterday
  if (diffInDays === 1) {
    return 'Yesterday';
  }

  // More than 2 days ago
  if (dateFormat?.length && DateFormats?.[dateFormat]) {
    return inputDate.format(DateFormats[dateFormat]);
  }

  return inputDate.format('L');
}
